import { useScroll } from 'hooks';
import { SpeakingSessionRoomStructureDto, SpeakingSlotDto, SpeakingSlotType } from 'api/planning';
import { useMemo } from 'react';
import { getMinSwaCapacity } from './utils/getMinSwaCapacity';
import { applyCapacityToSlots } from './utils/applyCapacityToSlots';

type Props = {
  structure: SpeakingSessionRoomStructureDto;
  structures: SpeakingSessionRoomStructureDto[];
  editing: boolean;
  onDelete: (id: number) => void;
  onChangeStructure: (value: SpeakingSessionRoomStructureDto) => void;
};

export const useRoomStructure = ({
  structure,
  structures,
  editing,
  onDelete,
  onChangeStructure,
}: Props) => {
  const { ref } = useScroll<HTMLDivElement>('main-speaking-slots-slider');

  const hasAnyBookedSlot = useMemo(
    () => structure.speakingSlots.some((x) => x.plannedCapacity > x.availableCapacity),
    [structure.speakingSlots]
  );

  const handleDeleteClick = () => {
    if (editing && !hasAnyBookedSlot) {
      onDelete(structure.id);
    }
  };

  const occupiedSlots = useMemo(
    () => structures.filter((x) => x.id !== structure.id).flatMap((x) => x.speakingSlots),
    [structures, structure]
  );

  const roomSwaSlots = useMemo(
    () =>
      structures
        .filter((x) => x.speakingSlotType === SpeakingSlotType.SWA)
        .flatMap((x) => x.speakingSlots),
    [structures]
  );

  const speakingSlotType = useMemo(() => {
    switch (structure.speakingSlotType) {
      case SpeakingSlotType.IPC:
        return { label: 'In-person Classic', color: 'grey' };
      case SpeakingSlotType.IPA:
        return { label: 'In-person Adapt', color: 'teal' };
      case SpeakingSlotType.RSS:
        return { label: 'Remote Speaking', color: 'pink' };
      case SpeakingSlotType.SWA:
        return { label: 'Avatar Speaking', color: 'violet' };
      default:
        return { label: 'Unknown', color: 'grey' };
    }
  }, [structure.speakingSlotType]);

  const onChangeSlots = (value: SpeakingSlotDto[]) => {
    onChangeStructure({ ...structure, speakingSlots: value });
  };

  const onChangeCapacity = (value: number) => {
    onChangeStructure({ ...structure, speakingSlots: applyCapacityToSlots(structure.speakingSlots, value) });
  };

  const minCapacity = useMemo(
    () => getMinSwaCapacity(structure.speakingSlots),
    [structure.speakingSlots]
  );

  return {
    ref,
    handleDeleteClick,
    occupiedSlots,
    roomSwaSlots,
    speakingSlotType,
    onChangeSlots,
    onChangeCapacity,
    minCapacity,
    hasAnyBookedSlot,
  };
};
